/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React from 'react';
import { connect } from 'react-redux';
import {
    object, string, shape, func, arrayOf, number,
} from 'prop-types';

import Grid from '@material-ui/core/Grid';
import withStyles from '@material-ui/core/styles/withStyles';
import Paper from '@material-ui/core/Paper';

import CommonRadioGroup from '../../Partials/GraphqlPDPSkus/CommonRadioGroup';
import { actions as tagManagerActions } from '../../../../../../state/ducks/TagManager';
import { getApplePayStatus } from '../../../../../../state/ducks/Checkout/ducks/Common/Common-Selectors';
import { getCurrencyData, getDataDictionary } from '../../../../../../state/ducks/App/App-Selectors';
import { getLanguageName } from '../../../../../../state/ducks/Member/ducks/Auth/helper/helper';
import skusTranslations from '../../translations/skusTranslations.json';

const styles = (theme) => ({
    root: {
        marginBottom: 0,
        width: '100%',
        color: theme?.palette?.common?.black,
        [theme.breakpoints.down('600')]: {
            marginBottom: 0,
        },
        boxShadow: 'none',
        borderRadius: '3px',
    },
    paperHeader: {
        boxSizing: 'border-box',
        width: '100%',
        padding: '12px 15px',
        fontWeight: '600',
        fontSize: '15px',
        lineHeight: '19px',
        color: theme.palette.colorPrimary,
        background: theme.palette.common?.lightPurple,
        borderRadius: '4px 4px 0 0',
        textTransform: 'uppercase',
        opacity: 0.9,
        [theme.breakpoints.down('sm')]: {
            padding: '12px 20px',
        },
    },
    paperBody: {
        boxSizing: 'border-box',
        padding: 0,
        '& label': {
            marginRight: '0px !important',
            '& span': {
                '&$radio': {
                    '&> span': {
                        position: 'relative',
                        display: 'block !important',
                        width: '20px',
                        height: '20px',
                        borderRadius: '50%',
                        background: `${theme.palette.common?.white} !important`,
                        border: `1px solid ${theme.palette.common?.black}`,
                        '& $root': {
                            display: 'none',
                        },
                        '&:last-child': {
                            display: 'none !important',
                        },
                        '& div': {
                            display: 'none',
                        },
                    },
                },
            },
        },
    },
    radio: {
        color: theme.palette.pdp?.radioButton,
        padding: '6px 12px',
        '&$checked': {
            color: theme.palette.pdp?.radioButton,
        },
        [theme.breakpoints.down('600')]: {
            '& svg': {
                fontSize: '24px',
            },
        },
    },
    checked: {
        color: theme.palette.pdp?.radioButton,
        display: 'block',
        '& span:before': {
            cursor: 'pointer',
            display: 'block',
            content: '"" !important',
            borderRadius: '100%',
            height: '11px',
            width: '11px',
            transition: 'background',
            background: theme.palette.pdp?.selectedRadio,
            position: 'absolute',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            visibility: 'visible',
        },
        '&+ span': {
            '&> div': {
                '&> span': {
                    fontWeight: '600',
                    '&:last-child': {
                        color: theme.palette.common?.black,
                        fontWeight: '600',
                    },
                },
            },
        },
    },
    skuLabelName: {
        color: theme.palette.common?.black,
        fontSize: '16px',
        lineHeight: '19px',
        marginRight: theme.spacing(2),
        [theme.breakpoints.down('600')]: {
            fontSize: '15px',
        },
    },
    skuLabelAmount: {
        fontSize: '16px',
        fontWeight: '400',
        lineHeight: '19px',
        whiteSpace: 'nowrap',
        [theme.breakpoints.down('600')]: {
            fontSize: '15px',
            textAlign: 'right',
        },
        '& .retail': {
            marginLeft: '5px',
            textDecoration: 'line-through',
            fontWeight: 700,
        },
        '& .sale': {
            marginLeft: '5px',
            color: theme.palette.pdp?.salePrice,
            fontWeight: 700,
        },
    },
    formLabel: {
        display: 'flex',
        '& >div': {
            width: '100%',
            justifyContent: 'space-between',
            display: 'flex',
        },
        '& span': {
            fontSize: '16px',
        },
    },

});

const GraphqlPlantsProductSkus = ({
    classes,
    skuData,
    handleSkuChange,
    selectedSku,
    trackEvent,
    presentation_family,
    brand,
    currencyData,
}) => {
    const language = getLanguageName();
    // @intention: Renders sale price if sale < retail
    // @return: html
    // TODO:
    // - use loop to determin sale vs retail in case order changes
    const renderSkuPrice = (sku) => {
        const retailPrice = sku?.prices?.[0]?.value || null;
        const salePrice = sku?.prices?.[1]?.value || null;
        const { currencySymbol } = currencyData;
        if (retailPrice && salePrice && salePrice < retailPrice) {
            return (
                <div>
                    <span className={classes.skuLabelName}>{language !== '/en' && brand?.code === 'CAN' ? skusTranslations?.[sku?.name?.trim()] || sku?.name : sku?.name}</span>
                    <span className={classes.skuLabelAmount}>
                        <span className={`${classes.skuLabelAmount} sale`}>{currencySymbol}{salePrice}</span>
                        {' '}
                        <span className={`${classes.skuLabelAmount} retail`}>{currencySymbol}{retailPrice}</span>
                    </span>
                </div>
            );
        }

        return (
            <div>
                <span className={classes.skuLabelName}>{language !== '/en' && brand?.code === 'CAN' ? skusTranslations?.[sku?.name?.trim()] || sku?.name : sku?.name}</span>
                <span className={classes.skuLabelAmount}>{currencySymbol}{retailPrice}</span>
            </div>
        );
    };

    return (
        <Paper elevation={1} className={classes.root}>
            <div className={classes.paperBody}>
                <Grid
                    container
                    direction="column"
                    justify="flex-start"
                >
                    <CommonRadioGroup
                        classes={classes}
                        skuData={skuData}
                        handleSkuChange={handleSkuChange}
                        trackEvent={trackEvent}
                        selectedSku={selectedSku}
                        getRadioLabel={renderSkuPrice}
                        presentation_family={presentation_family}
                    />
                </Grid>
            </div>
        </Paper>
    );
};

const mapStateToProps = (state) => ({
    applePayStatus: getApplePayStatus(state),
    currencyData: getCurrencyData(state),
    dataDictionary: getDataDictionary(state),
});

const mapDispatchToProps = {
    trackEvent: tagManagerActions.trackEvent,
};

GraphqlPlantsProductSkus.defaultProps = {
    brand: {},
    currencyData: {},
};

GraphqlPlantsProductSkus.propTypes = {
    classes: object.isRequired,
    skuData: arrayOf(
        shape({
            id: string.isRequired,
            name: string.isRequired,
            prices: arrayOf(
                shape({
                    type: string.isRequired,
                    value: number.isRequired,
                    __typename: string.isRequired,
                }),
            ).isRequired,
            __typename: string.isRequired,
        }),
    ).isRequired,
    handleSkuChange: func.isRequired,
    selectedSku: shape({
        id: string.isRequired,
        name: string.isRequired,
        prices: arrayOf(
            shape({
                type: string.isRequired,
                value: number.isRequired,
                __typename: string.isRequired,
            }),
        ).isRequired,
        __typename: string.isRequired,
    }).isRequired,
    trackEvent: func.isRequired,
    presentation_family: string.isRequired,
    brand: shape({
        domain: string,
        code: string,
    }),
    currencyData: shape({
        currencySymbol: string.isRequired,
    }),
};

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(styles)(GraphqlPlantsProductSkus));
